import { Badge } from "@designs-by-maryam/ui/components/badge";
import { Card, CardContent } from "@designs-by-maryam/ui/components/card";
import {
	formatCategoryName,
	getCategoryBadgeClassName,
	type ProjectCategory,
} from "@designs-by-maryam/ui/lib/category-styles";
import { cn } from "@designs-by-maryam/ui/lib/utils";
import { Calendar, MapPin } from "lucide-react";
import { motion } from "motion/react";
import { useMemo, useState } from "react";
import { getProjectCardHover, useSafeMotion } from "@/lib/motion";

// Re-export for backward compatibility
export type { ProjectCategory };

/**
 * Project data structure matching the Drizzle schema
 */
export type Project = {
	id: string;
	title: string;
	short_description: string | null;
	category: ProjectCategory;
	location: string | null;
	year: number | null;
	images: {
		id: string;
		url: string;
		thumbnail_url: string;
		alt: string;
		order: number;
	}[];
	is_featured: boolean;
	status: "draft" | "published" | "archived";
};

type ProjectCardProps = {
	project: Project;
	onClick?: () => void;
	className?: string;
};

/**
 * ProjectCard Component
 *
 * Displays a project card with image, category badge, title, location, and year.
 * Used in the featured projects grid on the homepage and portfolio pages.
 *
 * Features:
 * - Responsive image with lazy loading and blur-up effect
 * - Category badge with distinct colors and hover animations
 * - Advanced hover effects: image scale, overlay reveal, card lift with shadow
 * - Smooth reveal of project description on hover
 * - Respects prefers-reduced-motion
 * - Accessible with proper ARIA attributes
 * - Click handler for modal/detail view
 */
export function ProjectCard({ project, onClick, className }: ProjectCardProps) {
	const [imageLoaded, setImageLoaded] = useState(false);
	const { shouldReduce, hoverScaleSmall } = useSafeMotion();

	const mainImage = project.images[0];
	const imageUrl =
		mainImage?.thumbnail_url || mainImage?.url || "/placeholder-project.jpg";
	const imageAlt =
		mainImage?.alt ||
		`${project.title} - ${formatCategoryName(project.category)} project`;

	// Get motion variants from shared library - memoized for performance
	const variants = useMemo(
		() => getProjectCardHover(shouldReduce),
		[shouldReduce],
	);

	return (
		<motion.div
			animate="rest"
			className={className}
			initial="rest"
			variants={variants.card}
			whileHover="hover"
		>
			<Card
				aria-haspopup="dialog"
				aria-label={`View ${project.title} - ${formatCategoryName(project.category)} project details`}
				className={cn(
					"group cursor-pointer overflow-hidden border bg-card transition-shadow duration-300",
					// Enhanced shadow on hover
					"hover:shadow-2xl hover:shadow-primary/10 dark:hover:shadow-primary/20",
					className,
				)}
				data-testid="project-card"
				onClick={onClick}
				onKeyDown={(e) => {
					if ((e.key === "Enter" || e.key === " ") && onClick) {
						e.preventDefault();
						onClick();
					}
				}}
				role="button"
				tabIndex={0}
			>
				{/* Image Section */}
				<div className="relative aspect-[4/3] overflow-hidden bg-muted">
					{/* Loading skeleton with blur */}
					{!imageLoaded && (
						<div className="absolute inset-0 animate-pulse bg-gradient-to-br from-muted to-muted/50" />
					)}

					<motion.img
						alt={imageAlt}
						className={cn(
							"h-full w-full object-cover transition-opacity duration-500",
							imageLoaded ? "opacity-100" : "opacity-0",
						)}
						data-testid="project-card-image"
						height={300}
						loading="lazy"
						onLoad={() => setImageLoaded(true)}
						src={imageUrl}
						style={{ willChange: "transform" }}
						variants={variants.image}
						width={400}
					/>

					{/* Gradient overlay on hover */}
					<motion.div
						className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent"
						variants={variants.overlay}
					/>

					{/* Category Badge - animated on hover */}
					<motion.div
						className="absolute top-3 left-3"
						whileHover={hoverScaleSmall}
					>
						<Badge
							className={cn(
								"font-medium text-xs shadow-lg backdrop-blur-sm",
								getCategoryBadgeClassName(project.category),
							)}
							data-testid="project-card-badge"
						>
							{formatCategoryName(project.category)}
						</Badge>
					</motion.div>

					{/* Description overlay - reveals on hover */}
					{project.short_description && (
						<motion.div
							className="pointer-events-none absolute inset-x-0 bottom-0 p-4 text-white"
							variants={variants.overlay}
						>
							<p className="line-clamp-2 font-medium text-sm drop-shadow-lg">
								{project.short_description}
							</p>
						</motion.div>
					)}
				</div>

				{/* Content Section */}
				<CardContent className="p-4">
					<motion.div variants={variants.content}>
						{/* Title */}
						<h3
							className="mb-2 line-clamp-2 font-semibold text-foreground text-lg transition-colors group-hover:text-primary"
							data-testid="project-card-title"
						>
							{project.title}
						</h3>

						{/* Metadata Row */}
						<div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-muted-foreground text-sm">
							{/* Location */}
							{project.location && (
								<div
									className="flex items-center gap-1 transition-colors group-hover:text-foreground"
									data-testid="project-card-location"
								>
									<MapPin aria-hidden="true" className="h-4 w-4 shrink-0" />
									<span className="truncate">{project.location}</span>
								</div>
							)}

							{/* Year */}
							{project.year && (
								<div
									className="flex items-center gap-1 transition-colors group-hover:text-foreground"
									data-testid="project-card-year"
								>
									<Calendar aria-hidden="true" className="h-4 w-4 shrink-0" />
									<span>{project.year}</span>
								</div>
							)}
						</div>
					</motion.div>
				</CardContent>
			</Card>
		</motion.div>
	);
}

export default ProjectCard;
